import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import type { QuizSolveResult, QuizMediaObject, BaseQuizData } from '../../../types/legacyQuiz'

export interface OrderingChoice {
  key: string
  answer: string
  image?: QuizMediaObject | null
}

export interface OrderingQuizData extends BaseQuizData {
  type: 'ordering'
  choices: OrderingChoice[]
}

export interface UseOrderingQuizOptions {
  quizId: string | number
  quiz: OrderingQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  onAnswerChange?: (answers: string[]) => void
}

function areOrderingChoicesEqual(left: OrderingChoice[], right: OrderingChoice[]): boolean {
  return left.length === right.length && left.every((choice, index) => {
    const other = right[index]
    return choice.key === other.key
      && choice.answer === other.answer
      && choice.image?.signedUrl === other.image?.signedUrl
      && choice.image?.url === other.image?.url
  })
}

export function useOrderingQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange,
}: UseOrderingQuizOptions) {
  const [startTime] = useState(() => Date.now())
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false)
  const [isVertical, setIsVertical] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 900 : true
  )

  const isSolved = solveResult !== null && solveResult !== undefined
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers

  useEffect(() => {
    const handleResize = () => {
      setIsVertical(window.innerWidth < 900)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const computedInitialChoices = useMemo(() => {
    if (initialAnswersProp !== undefined) {
      const submittedChoices = initialAnswersProp
        .map(key => quiz.choices.find(c => c.key === key))
        .filter((c): c is OrderingChoice => c !== undefined)
      return submittedChoices.length > 0 ? submittedChoices : [...quiz.choices]
    }
    if (isViewSolved && solveResult?.correctAnswers && solveResult.correctAnswers.length > 0) {
      return solveResult.correctAnswers
        .map(key => quiz.choices.find(c => c.key === key))
        .filter((c): c is OrderingChoice => c !== undefined)
    }
    return [...quiz.choices]
  }, [quiz.choices, initialAnswersProp, isViewSolved, solveResult?.correctAnswers])

  const [orderedChoices, setOrderedChoices] = useState<OrderingChoice[]>(computedInitialChoices)
  const [submittedChoices, setSubmittedChoices] = useState<OrderingChoice[] | null>(null)
  const onAnswerChangeRef = useRef(onAnswerChange)

  useEffect(() => {
    onAnswerChangeRef.current = onAnswerChange
  }, [onAnswerChange])

  useEffect(() => {
    setOrderedChoices(prev =>
      areOrderingChoicesEqual(prev, computedInitialChoices) ? prev : computedInitialChoices
    )
  }, [computedInitialChoices])

  useEffect(() => {
    if (isSolved && !submittedChoices) {
      setSubmittedChoices(orderedChoices)
    }
  }, [isSolved, submittedChoices, orderedChoices])

  useEffect(() => {
    if (!isSolved) return
    if (shouldShowCorrectAnswers && solveResult?.correctAnswers && solveResult.correctAnswers.length > 0) {
      const reordered = solveResult.correctAnswers
        .map(key => quiz.choices.find(c => c.key === key))
        .filter((c): c is OrderingChoice => c !== undefined)
      setOrderedChoices(prev =>
        areOrderingChoicesEqual(prev, reordered) ? prev : reordered
      )
    } else if (submittedChoices) {
      setOrderedChoices(prev =>
        areOrderingChoicesEqual(prev, submittedChoices) ? prev : submittedChoices
      )
    }
  }, [shouldShowCorrectAnswers, isSolved, solveResult?.correctAnswers, quiz.choices, submittedChoices])

  useEffect(() => {
    onAnswerChangeRef.current?.(orderedChoices.map(c => c.key))
  }, [orderedChoices])

  const handleReorder = useCallback((oldIndex: number, newIndex: number) => {
    setOrderedChoices(prev => {
      const next = [...prev]
      const [removed] = next.splice(oldIndex, 1)
      next.splice(newIndex, 0, removed)
      return next
    })
  }, [])

  const canSubmit = true

  const handleSubmit = useCallback(() => {
    if (isSolved) return
    const elapsedTime = Date.now() - startTime
    const answers = orderedChoices.map(c => c.key)
    onSolve?.(quizId, answers, quiz.vars, elapsedTime)
  }, [isSolved, startTime, orderedChoices, onSolve, quizId, quiz.vars])

  return {
    orderedChoices,
    setOrderedChoices,
    isVertical,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleReorder,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved,
  }
}
